import { Debug } from './debug';
import { runInDebug } from '@ember/debug';

/**
 * Whether the panel already carries a name its author gave it.
 *
 * `@ariaLabel` is not the only way in — `...attributes` can carry an `aria-label` straight to the
 * root — and both owners bind the argument to that same attribute before splatting, so the DOM is
 * where either one lands. Asking the element rather than the argument is therefore both simpler and
 * truer: it is the rendered name that decides whether the panel needs one, and whether applying
 * `aria-labelledby` would overrule it.
 *
 * @param {Element} [baseElement] the owner's root element
 * @returns {boolean} true when the root carries a non-empty `aria-label`
 */
function hasAuthoredName(baseElement) {
  return Boolean(baseElement?.getAttribute('aria-label')?.trim());
}

const NAMING_ATTRIBUTES = ['aria-label', 'alt', 'title'];
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE']);

function isExcludedFromName(element) {
  return (
    element.getAttribute('aria-hidden') === 'true' ||
    element.hasAttribute('hidden') ||
    SKIPPED_TAGS.has(element.tagName.toUpperCase())
  );
}

function hasNameFromAttributes(element) {
  return (
    NAMING_ATTRIBUTES.some(attribute => element.getAttribute(attribute)?.trim()) ||
    element.hasAttribute('aria-labelledby') ||
    [...(element.labels ?? [])].some(label => label.textContent.trim())
  );
}

function namesFromContent(node) {
  if (node.nodeType === Node.TEXT_NODE) return Boolean(node.data.trim());

  return hasNameFromAttributes(node);
}

function filterNameNode(node) {
  if (node.nodeType === Node.ELEMENT_NODE && isExcludedFromName(node)) {
    return NodeFilter.FILTER_REJECT;
  }

  return namesFromContent(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
}

/**
 * Whether the element already has an accessible name: from `aria-label`, `aria-labelledby`, `alt`,
 * `title`, or an associated `<label>`, on the element or any descendant outside `aria-hidden` and
 * `hidden` subtrees, or from descendant text. An approximation of accname, not an implementation of it — see `hasAuthoredName`.
 *
 * @param {Element} [element]
 * @returns {boolean}
 */
export function hasAccessibleName(element) {
  if (!element) return false;
  if (hasNameFromAttributes(element)) return true;

  let walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    filterNameNode
  );

  return Boolean(walker.nextNode());
}

/**
 * Names a panel from the title its header rendered, or warns when it has no name at all.
 *
 * This cannot be a template binding. The header is a yielded block, so at the point the root
 * element's attributes are evaluated there is no way to know whether the consumer rendered it.
 * Binding `aria-labelledby` unconditionally leaves a dangling IDREF on every panel that omits the
 * block: name computation skips a reference resolving to nothing, so the element is unnamed either
 * way, but the invalid reference is a finding in its own right under `aria-valid-attr-value`.
 *
 * Both owners call this from `setup`, once the yielded subtree is in the DOM. It is a plain
 * function rather than a capability because the attribute belongs on the owner's root — the
 * element carrying the role — while the title it points at belongs to the chrome.
 *
 * @param {object} owner component rendering the panel; supplies `baseElement`, `componentName`,
 * `headerTitleId`, and `id`. `@ariaLabel` is read off the rendered root rather than the argument —
 * see `hasAuthoredName`
 * @param {string} warningId dedupe id for the development warning
 */
export function applyPanelAccessibleName(owner, warningId) {
  let { baseElement, componentName, headerTitleId, id } = owner;
  let title = baseElement?.querySelector(`#${CSS.escape(headerTitleId)}`);

  if (hasAuthoredName(baseElement)) return;

  if (!title) {
    runInDebug(() =>
      Debug.warn(
        `${componentName} has no accessible name. Render the yielded Header block or pass @ariaLabel [instance: ${id}]`,
        false,
        { id: warningId }
      )
    );

    return;
  }

  // Bails on an author-supplied `aria-labelledby`, which `...attributes` would otherwise have won
  // outright when this was a template binding.
  if (baseElement.hasAttribute('aria-labelledby')) return;

  baseElement.setAttribute('aria-labelledby', headerTitleId);
}

export default { applyPanelAccessibleName, hasAccessibleName };
